import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useSendMessageMutation } from '../../redux/apis/endpoints/messageApi';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import websocketService from '../../services/websocketService';

const MessageInput = ({ recipientId, productId, fulfillmentGroupId }) => {
  const [text, setText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const textAreaRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const { user } = useSelector(state => state.auth);
  const [sendMessage, { isLoading }] = useSendMessageMutation();

  // Auto resize textarea
  useEffect(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = '40px';
      textAreaRef.current.style.height = `${textAreaRef.current.scrollHeight}px`;
    }
  }, [text]);

  // Handle typing status
  useEffect(() => {
    if (!user || !recipientId) return;

    const isUserTyping = text.trim().length > 0;
    
    // Only emit if typing status changed
    if (isUserTyping !== isTyping) {
      setIsTyping(isUserTyping);
      
      // Emit typing status via websocket
      if (websocketService.isConnected()) {
        websocketService.emitTyping(user.id, recipientId, isUserTyping);
      }
    }
    
    // Reset typing status after 2 seconds of inactivity
    clearTimeout(typingTimeoutRef.current);
    if (isUserTyping) {
      typingTimeoutRef.current = setTimeout(() => {
        setIsTyping(false);
        if (websocketService.isConnected()) {
          websocketService.emitTyping(user.id, recipientId, false);
        }
      }, 2000);
    }
    
    return () => clearTimeout(typingTimeoutRef.current);
  }, [text, user, recipientId, isTyping]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const trimmedText = text.trim();
    if (!trimmedText || !recipientId || isLoading) return;
    
    try {
      setText('');
      
      // First send via WebSocket for immediate display
      if (websocketService.isConnected()) {
        websocketService.sendMessage({
          text: trimmedText,
          senderId: user.id,
          receiverId: recipientId,
          productId: productId || undefined,
          fulfillmentGroupId: fulfillmentGroupId || undefined
        });
      }
      
      // Then send via HTTP API
      await sendMessage({
        receiverId: recipientId,
        text: trimmedText,
        ...(productId && { productId }),
        ...(fulfillmentGroupId && { fulfillmentGroupId }),
        senderId: user.id
      }).unwrap();
      
      // Stop typing indicator
      if (websocketService.isConnected()) {
        websocketService.emitTyping(user.id, recipientId, false);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      // Optionally display an error notification
    }
  };

  const handleKeyDown = (e) => {
    // Send message on Enter key (without Shift)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <div className="px-4 py-3 border-t border-gray-200 bg-white">
      <form onSubmit={handleSubmit} className="flex items-center space-x-2">
        <div className="flex-1 min-w-0">
          <textarea
            ref={textAreaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            className="block w-full rounded-lg border-gray-300 shadow-sm focus:border-brand-teal focus:ring-brand-teal sm:text-sm resize-none py-2 px-3 max-h-32"
            rows={1}
            disabled={isLoading}
          />
        </div>
        
        <div className="flex-shrink-0">
          <button
            type="submit"
            disabled={!text.trim() || isLoading}
            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-teal ${
              !text.trim() || isLoading
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-brand-teal hover:bg-brand-teal-dark'
            }`}
          >
            <PaperAirplaneIcon className="h-4 w-4 mr-2" />
            <span>Send</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default MessageInput; 